import {
  ChangeGroup,
  InlineEditChangeGroupEntry,
  ChangeVisual,
  Change,
  InlineEditChangeFocusEventPayload,
  InlineEditModificationSource,
} from '@amzn/awscat-editor';
import { InlineEditItem, InlineEditResolution } from '@amzn/awscat-pattern-library-service-model';
import _ from 'lodash';
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useInlineEditsManager } from '../../../contexts/InlineEditsManagerContext';
import { useSuggestionNavigationService } from '../../../contexts/SuggestionNavigationServiceContext';
import { docIdEquals } from '../../../model/RichtextDocId';
import { InlineEditsManager } from '../../../services/InlineEditsManager';
import { sanitizeInlineEditReplacement } from '../../../services/InlineEditSanitizer';
import { InlineTextFilter } from '../../../services/InlineTextOperations';
import { SectionEditChanges } from '../../../services/SuggestionNavigationService';
import { useFilteredInlineEdits } from '../../v2-hooks/useFilteredInlineEdits';
import { scrollIntoComfortZone } from './Window';
import { Editor } from '@tiptap/core';
import { log } from '../../../loggers/Logger';
import FlashbarMessageBroker from '../../../common/FlashbarMessageBroker';
import { CHANGES_MODIFIED_THROTTLE, DEFAULT_INLINE_TEXT_FILTER, PopoverState } from './Constants';
import { useStableDocId } from './useStableDocId';

/**
 * Maps the InlineEditItem received from the AI agents to ChangeGroup for
 * displaying in the Tiptap editor
 */
function toChangeGroups(editor: Editor, inlineEdits: readonly InlineEditItem[]): ChangeGroup[] {
  return inlineEdits
    .filter((edit) => !edit.resolved)
    .reduce<ChangeGroup[]>((groups, edit) => {
      try {
        // Sanitize agent output before deserialization — nodeFromJSON validates
        // node/mark types but never attribute values, so a hostile edit is
        // rejected here rather than rendered/persisted.
        groups.push({
          id: edit.editId,
          nodeId: edit.nodeId,
          replacementNode: editor.schema.nodeFromJSON(
            sanitizeInlineEditReplacement(edit.replacement),
          ),
        });
      } catch (e) {
        log.warn({ err: e }, `[toChangeGroups] Rejected inline edit ${edit.editId}:`);
      }
      return groups;
    }, []);
}

function toNavigationChanges(
  inlineEdits: readonly InlineEditItem[],
  changesByEdit: ReadonlyMap<string, SectionEditChanges>,
): readonly SectionEditChanges[] {
  return inlineEdits
    .filter((edit) => !edit.resolved)
    .map(
      (edit) =>
        changesByEdit.get(edit.editId) ?? {
          inlineEditId: edit.editId,
          changes: [],
        },
    );
}

/** Resolves inline edits that have no changes left in the editor. */
export async function resolveModifiedEdits(
  entries: readonly InlineEditChangeGroupEntry[],
  manager: InlineEditsManager,
  source: InlineEditModificationSource,
): Promise<void> {
  const opened = manager.openedEdits;
  if (!opened) return;

  // Collect resolution attempts so a whole pass reports at most one notice.
  const resolutions: Promise<void>[] = [];
  for (const entry of entries) {
    // Only document edits imply acceptance. Accept/reject commands report their
    // outcome separately, while a rebuild with no diff means the content already
    // matches the suggestion.
    if (entry.changes.length > 0) continue;
    const isNoDiff = entry.originalChanges.length === 0;
    if (!isNoDiff && source !== 'documentEdit') continue;

    const editId = entry.changeGroup.id;
    const edit = opened.edits.find((e) => e.editId === editId);
    if (edit?.resolved) continue;

    // The user interacted with the inline edit, implicitly accepting it by
    // manually typing the resolution rather than using the accept/reject buttons.
    resolutions.push(
      manager.tryResolveEdit(editId, InlineEditResolution.ACCEPTED).catch((error) => {
        log.error({ err: error }, `[useInlineEditHandler] Failed to resolve edit "${editId}"`);
        throw error;
      }),
    );
  }

  // A .catch fires asynchronously after this loop, so we await before deciding
  // whether to notify — a single notice covers however many failed this pass.
  const results = await Promise.allSettled(resolutions);
  if (results.some((r) => r.status === 'rejected')) {
    // Implicit (typed-over) resolution — surface a low-severity, non-blocking notice.
    FlashbarMessageBroker.create().pushWarnMessage(
      "Your edit was applied, but we couldn't mark the suggestion resolved. It may reappear until you retry.",
    );
  }
}

/**
 * Skips an insert that immediately follows a delete ending at the same position.
 * The delete represents the logical navigation target; the trailing insert is
 * just the replacement text occupying the same location. Only the first insert
 * after a delete is skipped; subsequent inserts are kept.
 */
export function collapseReplacementPairs(
  changes: readonly ChangeVisual[],
): readonly ChangeVisual[] {
  const result: ChangeVisual[] = [];

  for (let i = 0; i < changes.length; i++) {
    const current = changes[i];
    if (!current) continue;

    const prev = changes[i - 1];

    // Skip the insert when it immediately follows a delete ending at the same position.
    if (current.type === 'insert' && prev?.type === 'delete' && current.fromPos === prev.toPos) {
      continue;
    }

    result.push(current);
  }

  return result;
}

/**
 * Resolves the navigation target when a user clicks a change inside the editor.
 * Insertions are grouped with their preceding deletion for navigation purposes,
 * so clicking an insertion should land on the deletion — but only when the
 * immediately preceding change is in fact a deletion.
 */
export function resolveClickTarget(changeId: number, changes: readonly Change[]): number {
  const clickedIndex = changes.findIndex((c) => c.id === changeId);
  const clickedChange = clickedIndex >= 0 ? changes[clickedIndex] : undefined;
  const preceding = clickedIndex > 0 ? changes[clickedIndex - 1] : undefined;

  if (clickedChange?.type === 'insert' && preceding?.type === 'delete') {
    return preceding.id;
  }

  return changeId;
}

export interface InlineEditIntegration {
  readonly syncModifiedChanges: (
    entries: readonly InlineEditChangeGroupEntry[],
    source: InlineEditModificationSource,
  ) => void;
  readonly focusChange: (payload: InlineEditChangeFocusEventPayload) => void;
  readonly popover: PopoverState | undefined;
  readonly hidePopover: () => void;
}

export function useInlineEditIntegration(
  editor: Editor | undefined,
  inlineTextFilter: InlineTextFilter | undefined,
): InlineEditIntegration {
  // Preps
  const [popover, setPopover] = useState<PopoverState | undefined>(undefined);
  const documentId = useStableDocId(inlineTextFilter);
  const inlineEditManager = useInlineEditsManager();
  const navigationService = useSuggestionNavigationService();
  const sentInlineEditIds = useRef<Set<string>>(new Set());
  // The editor the sent edits belong to; a different editor starts empty.
  const sentToEditor = useRef<Editor | undefined>(undefined);
  // Optimization: create a Popover ref so Popover state can be accessed
  // from inside useCallback, without this, when popover state changes,
  // the callback will change and causes Tiptap to re-render
  const popoverRef = useRef<PopoverState | undefined>(undefined);
  popoverRef.current = popover;
  // Guards against the re-entry cycle: forceClickChange → focusChange → moveInto → forceClickChange.
  const isProgrammaticClickRef = useRef(false);

  // Retrieve inline edits
  const inlineEdits = useFilteredInlineEdits(inlineTextFilter ?? DEFAULT_INLINE_TEXT_FILTER);

  // When the editor is ready or the unresolved edits change, then remove the
  // resolved edits and add the unresolved ones. The editor skips edits it already
  // shows, keeping the changes the user accepted or rejected in them, and retries
  // edits it skipped before, such as one nested in a now-removed edit.
  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      // A new editor starts empty, so every edit must be sent to it again.
      sentInlineEditIds.current = new Set();
      sentToEditor.current = undefined;
      return;
    }

    if (sentToEditor.current !== editor) {
      sentInlineEditIds.current = new Set();
      sentToEditor.current = editor;
    }

    const sentIds = sentInlineEditIds.current;
    const unresolvedEdits = (inlineEdits ?? []).filter((e) => !e.resolved);
    const nextIds = new Set(unresolvedEdits.map((e) => e.editId));
    const removedIds = [...sentIds].filter((id) => !nextIds.has(id));
    const hasAddedEdits = [...nextIds].some((id) => !sentIds.has(id));
    if (removedIds.length === 0 && !hasAddedEdits) return;
    sentInlineEditIds.current = nextIds;

    if (removedIds.length > 0) {
      editor.commands.removeChangeGroups(removedIds);
    }
    if (unresolvedEdits.length > 0) {
      editor.commands.addChangeGroups(toChangeGroups(editor, unresolvedEdits));
    }
  }, [editor, inlineEdits]);

  // Accumulated changes per edit group, keyed by editId.
  // The callback only receives groups that changed, so we accumulate to report the full picture.
  const navigatableChangesPerInlineEdit = useRef<Map<string, SectionEditChanges>>(new Map());

  // Clear this document's navigation report when the editor unmounts.
  useEffect(() => {
    return () => {
      if (documentId) {
        navigationService.clear({ type: 'doc_inline_edit', documentId });
      }
    };
  }, [navigationService, documentId]);

  // Register edit ownership immediately; TipTap fills in clickable change geometry afterward.
  useEffect(() => {
    if (!documentId) return;
    navigationService.report({
      type: 'doc_inline_edit',
      documentId,
      changes: toNavigationChanges(inlineEdits, navigatableChangesPerInlineEdit.current),
    });
  }, [navigationService, documentId, inlineEdits]);

  // When the editor generates the inline edit changes (the diff)
  // Report it to NavigationService so user can navigate here
  const reportNavigationChanges = useCallback(
    (entries: readonly InlineEditChangeGroupEntry[]) => {
      // Update tracked changes from entries and re-report to navigation.
      for (const entry of entries) {
        navigatableChangesPerInlineEdit.current.set(entry.changeGroup.id, {
          inlineEditId: entry.changeGroup.id,
          changes: collapseReplacementPairs(entry.changes),
        });
      }

      if (documentId) {
        navigationService.report({
          type: 'doc_inline_edit',
          documentId,
          changes: toNavigationChanges(inlineEdits, navigatableChangesPerInlineEdit.current),
        });
      }
    },
    [navigationService, documentId, inlineEdits],
  );
  const reportNavigationChangesThrottled = useMemo(
    () =>
      _.throttle(reportNavigationChanges, CHANGES_MODIFIED_THROTTLE, {
        leading: false,
        trailing: true,
      }),
    [reportNavigationChanges],
  );

  // When unmount, cancel pending throttled calls.
  useEffect(() => {
    return () => reportNavigationChangesThrottled.cancel();
  }, [reportNavigationChangesThrottled]);

  // When the editor's changes are modified, then resolve consumed edits right away
  // and throttle only the navigation report. A throttled call keeps just the last
  // source, so an accept right after a typed-over edit would hide the document edit.
  const syncModifiedChanges = useCallback(
    (entries: readonly InlineEditChangeGroupEntry[], source: InlineEditModificationSource) => {
      void resolveModifiedEdits(entries, inlineEditManager, source);
      reportNavigationChangesThrottled(entries);
    },
    [inlineEditManager, reportNavigationChangesThrottled],
  );

  // When Tiptap the user clicks on the changes inside tiptap editor, or when the
  // "Change" is moved, handle that..
  const focusChange = useCallback(
    (payload: InlineEditChangeFocusEventPayload) => {
      if (payload.type === 'click') {
        const targetChangeId = resolveClickTarget(payload.changeId, payload.changes);

        // When the click was triggered programmatically by forceClickChange (navigation-driven),
        // skip moveInto to break the re-entry cycle.
        if (isProgrammaticClickRef.current) {
          isProgrammaticClickRef.current = false;
        } else {
          navigationService.moveInto({
            type: 'doc_inline_edit',
            inlineEditId: payload.changeGroup.id,
            changeId: targetChangeId,
          });
        }

        // Open the popover with fresh DOM position from the click payload.
        const targetChange = payload.changes.find((c) => c.id === targetChangeId);
        if (targetChange) {
          setPopover({
            type: 'inline_edit',
            target: targetChange.domRect,
            inlineEditId: payload.changeGroup.id,
            changeId: targetChangeId,
            shouldResolveInlineEdit: collapseReplacementPairs(payload.changes).length === 1,
          });
          scrollIntoComfortZone(targetChange.domRect);
        }
      }

      // When the editor reports updated changes and the popover is showing for
      // the same change group, then move the popover and recount what remains,
      // since typing over another change can leave the open one as the last.
      if (payload.type === 'update') {
        const current = popoverRef.current;
        if (!current || current.type !== 'inline_edit') return;
        if (current.inlineEditId !== payload.changeGroup.id) return;

        const updatedChange = payload.changes.find((c) => c.id === current.changeId);
        if (updatedChange) {
          setPopover({
            ...current,
            target: updatedChange.domRect,
            shouldResolveInlineEdit: collapseReplacementPairs(payload.changes).length === 1,
          });
        }
      }
    },
    [navigationService],
  );

  // When navigation moves to an inline edit, trigger a click on the change via the editor command.
  useEffect(() => {
    if (!documentId) {
      return;
    }
    const unsub = navigationService.on('navigationItemChanged', ({ item }) => {
      // Dismiss popover when navigation moves to a different document.
      if (item.type !== 'doc_inline_edit' || !docIdEquals(item.documentId, documentId)) {
        setPopover(undefined);
        return;
      }

      // Skip if the popover is already showing this exact change.
      const current = popoverRef.current;
      if (
        current?.type === 'inline_edit' &&
        current.inlineEditId === item.inlineEditId &&
        current.changeId === item.change.id
      ) {
        return;
      }

      // Programmatically focus the change in the editor.
      if (editor) {
        isProgrammaticClickRef.current = true;
        editor.commands.forceClickChange(item.inlineEditId, item.change.id);
      }
    });
    return () => {
      unsub();
    };
  }, [navigationService, editor, documentId]);

  const hidePopover = useCallback(() => {
    setPopover(undefined);
  }, []);

  return {
    syncModifiedChanges,
    focusChange,
    popover,
    hidePopover,
  };
}
